import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useQuery as useQ } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Download, UserCheck, Truck, XCircle, Pencil, Check, X, Trash2, Plus } from 'lucide-react';
import api from '../api';
import { can } from '../auth';
import NotesEditor from '../components/NotesEditor';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ParcelChanges from '../components/ParcelChanges';
const LocationPicker = lazy(() => import('../components/LocationPicker'));

/* ── WhatsApp helpers ─────────────────────────────────────────────── */
function waNumber(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? '27' + digits.slice(1) : digits;
}

function substituteVars(template, parcel) {
  if (!template) return '';

  // Pull failure reason from the most recent failed_delivery event (if any)
  const lastFail = (parcel.events || []).filter(e => e.event_type === 'failed_delivery').pop();
  const reasonLabel = lastFail
    ? ({ client_not_home: 'Client not at home', out_of_time: 'Out of time',
         vehicle_breakdown: 'Vehicle breakdown', wrong_address: 'Wrong address',
         refused: 'Refused', other: 'Other' }[lastFail.failure_reason] || lastFail.failure_reason || '')
    : '';

  const priceStr = parcel.final_price
    ? `R${parseFloat(parcel.final_price).toFixed(2)}` : '';
  const dateStr = parcel.scheduled_delivery
    ? new Date(parcel.scheduled_delivery).toLocaleDateString('en-ZA') : '';
  const statusStr = STATUS_LABELS[parcel.status] || parcel.status || '';

  const trackUrl = parcel.tracking_number
    ? `${window.location.origin}/track/${parcel.tracking_number}` : '';

  // All variable names — supports BOTH {var} and {{var}} formats
  const vars = {
    // Short / friendly names (what users type in templates)
    name:               parcel.recipient_name  || '',
    recipient:          parcel.recipient_name  || '',
    tracking:           parcel.tracking_number || '',
    trackUrl:           trackUrl,
    track_url:          trackUrl,
    sender:             parcel.sender_name     || '',
    status:             statusStr,
    description:        parcel.description     || '',
    price:              priceStr,
    driver:             parcel.driver_name     || '',
    date:               dateStr,
    reason:             reasonLabel,
    notes:              lastFail?.failure_notes || '',
    // Long / original names ({{double-brace}} style)
    recipient_name:     parcel.recipient_name  || '',
    tracking_number:    parcel.tracking_number || '',
    sender_name:        parcel.sender_name     || '',
    driver_name:        parcel.driver_name     || '',
    scheduled_delivery: dateStr,
    failure_reason:     reasonLabel,
    failure_notes:      lastFail?.failure_notes || '',
  };

  // Replace {{var}} and {var} in one pass
  return template.replace(/\{\{(\w+)\}\}|\{(\w+)\}/g, (match, p1, p2) => {
    const key = p1 || p2;
    return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match;
  });
}

function WhatsAppIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

/* ── Static lookup tables ─────────────────────────────────────────── */
const STATUS_COLORS = {
  created: 'bg-gray-100 text-gray-700', collected: 'bg-blue-100 text-blue-700',
  at_depot: 'bg-purple-100 text-purple-700', out_for_delivery: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-green-100 text-green-700', failed_delivery: 'bg-red-100 text-red-700',
  returned_to_depot: 'bg-orange-100 text-orange-700', rescheduled: 'bg-indigo-100 text-indigo-700',
  cancelled: 'bg-red-200 text-red-900',
};
const STATUS_LABELS = {
  created: 'Created', collected: 'Collected', at_depot: 'At Depot',
  out_for_delivery: 'Out for Delivery', delivered: 'Delivered',
  failed_delivery: 'Failed Delivery', returned_to_depot: 'Returned to Depot',
  rescheduled: 'Rescheduled', cancelled: 'Cancelled',
};
const FAILURE_REASONS = ['client_not_home', 'out_of_time', 'vehicle_breakdown', 'wrong_address', 'refused', 'other'];
const FAILURE_LABELS = {
  client_not_home: 'Client not at home', out_of_time: 'Out of time',
  vehicle_breakdown: 'Vehicle breakdown', wrong_address: 'Wrong address',
  refused: 'Refused', other: 'Other',
};
const EVENT_LABELS_WA = {
  collected: 'Collected', at_depot: 'At Depot', out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered', failed_delivery: 'Failed Delivery',
};

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-800 text-right max-w-xs">{value}</span>
    </div>
  );
}

/* ── WhatsApp panel ───────────────────────────────────────────────── */
function WhatsAppPanel({ parcel }) {
  const [selectedIdx, setSelectedIdx] = useState('');
  const [customMsg, setCustomMsg]     = useState('');

  const { data: waTemplates = [] } = useQ({
    queryKey: ['wa-templates'],
    queryFn: () => api.get('/notification-templates/whatsapp').then((r) => r.data),
  });

  const preview = useMemo(() => {
    if (selectedIdx === '') return customMsg;
    const tpl = waTemplates[parseInt(selectedIdx)];
    return tpl ? substituteVars(tpl.body, parcel) : '';
  }, [selectedIdx, customMsg, waTemplates, parcel]);

  const phone = waNumber(parcel.recipient_phone);

  const handleTemplateChange = (idx) => {
    setSelectedIdx(idx);
    if (idx !== '') {
      const tpl = waTemplates[parseInt(idx)];
      setCustomMsg(tpl ? substituteVars(tpl.body, parcel) : '');
    }
  };

  const openWhatsApp = () => {
    if (!phone) return toast.error('Recipient has no phone number');
    if (!preview.trim()) return toast.error('Message is empty');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(preview)}`, '_blank');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Green header */}
      <div className="bg-[#25D366] px-5 py-3 flex items-center gap-2">
        <WhatsAppIcon size={18} />
        <h3 className="font-semibold text-white text-sm">Send WhatsApp Message</h3>
        {parcel.recipient_phone && (
          <span className="ml-auto text-white/80 text-xs">{parcel.recipient_phone}</span>
        )}
      </div>

      <div className="p-5 space-y-3">
        {!parcel.recipient_phone && (
          <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">
            Recipient has no phone number — add one on the Clients page first.
          </p>
        )}

        {/* Template picker */}
        {waTemplates.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Choose template</label>
            <select
              value={selectedIdx}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="">— blank / custom message —</option>
              {waTemplates.map((tpl, i) => (
                <option key={i} value={i}>
                  {EVENT_LABELS_WA[tpl.event_type] || tpl.event_type}
                </option>
              ))}
            </select>
          </div>
        )}

        {waTemplates.length === 0 && (
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            No WhatsApp templates saved yet — go to Settings → Templates to create some.
          </p>
        )}

        {/* Editable message */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Message {selectedIdx !== '' && <span className="text-gray-400">(edit before sending)</span>}
          </label>
          <textarea
            rows={5}
            value={customMsg}
            onChange={(e) => { setCustomMsg(e.target.value); setSelectedIdx(''); }}
            placeholder="Type a message or choose a template above..."
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
          />
        </div>

        <button
          onClick={openWhatsApp}
          disabled={!phone || !customMsg.trim()}
          className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5e] disabled:opacity-40 text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
        >
          <WhatsAppIcon size={16} />
          Open in WhatsApp
        </button>

        <p className="text-xs text-gray-400 text-center">
          Opens WhatsApp with the message pre-filled — you confirm before sending.
        </p>

        {/* Variable reference */}
        <details className="group">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
            <span className="text-gray-300 group-open:rotate-90 inline-block transition-transform">▶</span>
            Available variables for templates
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {[
              ['{name}',        'Recipient name'],
              ['{tracking}',    'Tracking number'],
              ['{trackUrl}',    'Tracking link'],
              ['{sender}',      'Sender name'],
              ['{status}',      'Current status'],
              ['{reason}',      'Failure reason'],
              ['{price}',       'Parcel price'],
              ['{driver}',      'Driver name'],
              ['{date}',        'Delivery date'],
              ['{description}', 'Description'],
            ].map(([v, desc]) => (
              <div key={v} className="flex items-center gap-1.5">
                <code className="text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 font-mono">{v}</code>
                <span className="text-xs text-gray-400">{desc}</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

/* ── Boxes on this pickup ─────────────────────────────────────────────
   One collection is often several boxes belonging to the same order and the
   same invoice. Each box carries one of the barcode stickers already printed
   (e.g. OFF0005, OFF0006, OFF0007) — no special "box 2 of 5" labels needed.
   Scanning a box moves only that box; the pickup completes when all of them
   have been scanned, so a box left behind gets noticed. */
function BoxesPanel({ parcel, tracking, qc }) {
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState({});
  const [holder, setHolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [movingBox, setMovingBox] = useState(null);

  const boxes = parcel.pieces || [];
  const multi = boxes.length > 0;

  // Only stickers that are issued and not yet used anywhere
  const { data: avail = { holders: [] } } = useQuery({
    queryKey: ['barcodes-available'],
    queryFn: () => api.get('/barcodes/available').then((r) => r.data),
    enabled: adding,
  });
  const list = avail.holders.find((h) => h.holder_key === holder)?.numbers || [];
  const picked = Object.keys(chosen).filter((k) => chosen[k]);

  const add = async () => {
    if (!picked.length) return toast.error('Tick the barcodes on the other boxes');
    setBusy(true);
    try {
      const { data } = await api.post(`/parcels/${tracking}/pieces`, { barcodes: picked });
      if (data.rejected?.length) {
        toast.error(`${data.rejected.length} could not be used: ${data.rejected.map((r) => `${r.barcode} (${r.reason})`).join(', ')}`);
      }
      if (data.added?.length) toast.success(`${data.added.length} box${data.added.length === 1 ? '' : 'es'} added — ${data.total_boxes} in this pickup`);
      setChosen({}); setAdding(false);
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not add those boxes');
    } finally { setBusy(false); }
  };

  // Move one box by hand. The parcel follows only when every box agrees, the
  // same rule scanning uses — this is not a way to advance the consignment
  // while a box is still out.
  const setBoxStatus = async (b, status) => {
    if (status === b.status) return;
    setMovingBox(b.id);
    try {
      const { data } = await api.patch(`/parcels/${tracking}/pieces/${b.id}/status`, { status });
      toast.success(
        data.parcel_status_changed
          ? `All ${data.boxes} boxes are ${STATUS_LABELS[status] || status} — the pickup moved too`
          : `Box ${data.box} set to ${STATUS_LABELS[status] || status} (${data.at_stage} of ${data.boxes})`
      );
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change that box');
    } finally { setMovingBox(null); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove box ${b.piece_no} (${b.barcode}) from this pickup?`)) return;
    try {
      await api.delete(`/parcels/${tracking}/pieces/${b.id}`);
      toast.success('Box removed');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch { toast.error('Could not remove it'); }
  };

  // The pickup only advances once every box has, so a box whose status differs
  // from the pickup's is one that has already been scanned onward.
  const ahead = boxes.filter((b) => b.status !== parcel.status).length;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-gray-700 flex items-center gap-2">
          📦 {multi ? `${boxes.length} boxes in this pickup` : 'One box'}
        </h3>
        {!adding && (
          <button onClick={() => setAdding(true)}
            className="text-xs border border-gray-300 hover:border-orange-400 rounded-lg px-3 py-1.5 text-gray-700">
            + Add another box
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {multi
          ? 'Scanning any box marks that box only. The pickup is done when every box has been scanned.'
          : 'Collecting several boxes for the same order? Add their barcode stickers here — one price, one invoice.'}
      </p>

      {multi && (
        <>
          {ahead > 0 && (
            <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 mb-3">
              {ahead} of {boxes.length} boxes have been scanned onward — the other{' '}
              {boxes.length - ahead === 1 ? 'one is' : `${boxes.length - ahead} are`} still
              {' '}{String(parcel.status).replace(/_/g, ' ')}. The pickup stays here until every box is scanned.
            </p>
          )}
          <div className="border rounded-lg divide-y mb-3">
            {boxes.map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="text-gray-400 text-xs w-14">Box {b.piece_no}</span>
                <span className="font-mono text-xs font-semibold text-gray-800">{b.barcode}</span>
                {b.barcode.toUpperCase() === String(parcel.tracking_number).toUpperCase() && (
                  <span className="text-[10px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">main</span>
                )}
                {/* Scanning is the normal way a box moves, but a mis-scan or a
                    sticker too damaged to read would otherwise leave the whole
                    pickup stuck — the consignment only advances once every box
                    has. This is the way out of that from the office. */}
                <select
                  value={b.status}
                  disabled={movingBox === b.id}
                  onChange={(e) => setBoxStatus(b, e.target.value)}
                  className={`ml-auto border-0 rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50 ${
                    STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {Object.entries(STATUS_LABELS).map(([k, lbl]) => (
                    <option key={k} value={k}>{lbl}</option>
                  ))}
                </select>
                <button onClick={() => remove(b)} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
              </div>
            ))}
          </div>
        </>
      )}

      {adding && (
        <div className="border-2 border-orange-200 rounded-lg p-3 space-y-3">
          <p className="text-xs text-gray-500">
            Pick the stickers you put on the other boxes. Only unused issued barcodes are listed.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Barcode book</label>
              <select value={holder} onChange={(e) => { setHolder(e.target.value); setChosen({}); }}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                <option value="">— Choose holder —</option>
                {avail.holders.map((h) => (
                  <option key={h.holder_key} value={h.holder_key}>{h.holder_name} ({h.numbers.length})</option>
                ))}
              </select>
            </div>
            <div className="flex items-end text-xs text-gray-400">
              {picked.length > 0 ? `${picked.length} selected` : 'Tick the barcodes below'}
            </div>
          </div>
          {holder && (
            <div className="border rounded-lg max-h-56 overflow-y-auto divide-y">
              {list.map((n) => (
                <label key={n} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-orange-50 cursor-pointer">
                  <input type="checkbox" checked={!!chosen[n]}
                    onChange={(e) => setChosen((c) => ({ ...c, [n]: e.target.checked }))}
                    className="accent-orange-500" />
                  <span className="font-mono text-xs">{n}</span>
                </label>
              ))}
              {list.length === 0 && <p className="px-3 py-3 text-xs text-gray-400">No unused barcodes for this holder.</p>}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={add} disabled={busy || !picked.length}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
              {busy ? 'Adding…' : `Add ${picked.length || ''} box${picked.length === 1 ? '' : 'es'}`}
            </button>
            <button onClick={() => { setAdding(false); setChosen({}); }}
              className="border rounded-lg px-4 text-sm text-gray-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Depot weigh-in & pricing ─────────────────────────────────────────
   Chargeable weight = the greater of actual weight and volumetric weight
   (L × W × H in cm ÷ 5000, same as Fastway). The matching price tier is
   applied automatically; a manual price overrides it. */
function WeighPricePanel({ parcel, tracking, qc }) {
  const [weight, setWeight] = useState(parcel.weight || '');
  const [length, setLength] = useState(parcel.length || '');
  const [width,  setWidth]  = useState(parcel.width  || '');
  const [height, setHeight] = useState(parcel.height || '');
  const [manual, setManual] = useState('');
  const [saving, setSaving] = useState(false);

  // A multi-box pickup can be weighed as one load (everything on the scale at
  // once) or box by box. Both end in one chargeable weight and one price.
  const boxes = parcel.pieces || [];
  const [mode, setMode] = useState('load'); // 'load' | 'boxes'
  const [boxVals, setBoxVals] = useState({});
  useEffect(() => {
    setBoxVals(Object.fromEntries(boxes.map((b) => [b.id, {
      weight: b.weight ?? '', length: b.length ?? '', width: b.width ?? '', height: b.height ?? '',
    }])));
    // Several boxes: weigh each one by default, a row per box
    setMode(boxes.length > 0 ? 'boxes' : 'load');
  }, [parcel.tracking_number, boxes.length]);
  const setBox = (id, k) => (e) =>
    setBoxVals((v) => ({ ...v, [id]: { ...(v[id] || {}), [k]: e.target.value } }));

  const { data: tiers = [] } = useQ({
    queryKey: ['price-tiers'],
    queryFn: () => api.get('/price-tiers').then((r) => r.data),
  });
  const { data: billing = {} } = useQ({
    queryKey: ['billing-settings'],
    queryFn: () => api.get('/settings/billing').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const overKg = parseFloat(billing.billing_over_kg) > 0 ? parseFloat(billing.billing_over_kg) : 100;
  const overRate = parseFloat(billing.billing_over_rate) >= 0 ? parseFloat(billing.billing_over_rate) : 7;

  const perBox = mode === 'boxes' && boxes.length > 0;

  const singleW = parseFloat(weight) || 0;
  const L = parseFloat(length) || 0;
  const W = parseFloat(width)  || 0;
  const H = parseFloat(height) || 0;

  // Volumetric has to be worked out per box and then added up — three 30 cm
  // cubes are not one 90 cm cube, and charging them as one would overcharge.
  // Each box's own numbers, for the row under its inputs and the totals table
  const boxStats = boxes.map((b) => {
    const v = boxVals[b.id] || {};
    const bw = parseFloat(v.weight) || 0;
    const bL = parseFloat(v.length) || 0, bW = parseFloat(v.width) || 0, bH = parseFloat(v.height) || 0;
    const bVol = (bL > 0 && bW > 0 && bH > 0) ? (bL * bW * bH) / 5000 : 0;
    return { id: b.id, piece_no: b.piece_no, barcode: b.barcode, weight: bw, L: bL, W: bW, H: bH, vol: bVol,
      chargeable: Math.max(bw, bVol), measured: bw > 0 || bVol > 0 };
  });
  const boxTotals = boxStats.reduce((acc, x) => ({
    weight: acc.weight + x.weight, vol: acc.vol + x.vol, measured: acc.measured + (x.measured ? 1 : 0),
  }), { weight: 0, vol: 0, measured: 0 });

  const w          = perBox ? boxTotals.weight : singleW;
  const volumetric = perBox ? boxTotals.vol
                            : ((L > 0 && W > 0 && H > 0) ? (L * W * H) / 5000 : 0);
  const chargeable = Math.max(w, volumetric);
  const inList = chargeable > 0
    ? [...tiers]
        .filter(t => t.max_weight == null || parseFloat(t.max_weight) >= chargeable)
        .sort((a, b) => (parseFloat(a.max_weight) || 1e9) - (parseFloat(b.max_weight) || 1e9))[0]
    : null;
  // Heavier than the list: top tier + the per-kg rate for every started kg over
  const topTier = [...tiers].filter(t => t.max_weight != null && parseFloat(t.max_weight) <= overKg)
    .sort((a, b) => parseFloat(b.max_weight) - parseFloat(a.max_weight))[0];
  const extraKg = !inList && chargeable > overKg ? Math.ceil(chargeable - overKg - 1e-9) : 0;
  const matchedTier = inList
    ? inList
    : (extraKg > 0 && topTier
      ? { ...topTier, name: `${topTier.name} + ${extraKg} kg × R${overRate.toFixed(2)}`, price: parseFloat(topTier.price) + extraKg * overRate }
      : null);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/parcels/${tracking}/measure`, {
        ...(perBox
          ? { boxes: boxes.map((b) => ({ id: b.id, ...(boxVals[b.id] || {}) })) }
          : { weight: singleW || undefined, length: L || undefined,
              width: W || undefined, height: H || undefined }),
        manual_price: manual !== '' ? parseFloat(manual) : undefined,
      });
      toast.success(`Priced at R${parseFloat(data.price).toFixed(2)} (${data.price_source === 'manual' ? 'manual price' : String(data.price_source).startsWith('over:') ? String(data.price_source).slice(5) : `tier ${data.price_source}`})`);
      setManual('');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-700 mb-1 flex items-center gap-2">⚖️ Weigh &amp; Price (depot)</h3>
      <p className="text-xs text-gray-400 mb-3">
        Weigh the parcel and measure it in cm. The price uses whichever is heavier: the scale weight or the
        volumetric weight (L × W × H ÷ 5000).
      </p>

      {boxes.length > 0 && (
        <div className="flex gap-2 mb-3">
          {[['load', `Whole load (${boxes.length} boxes together)`], ['boxes', 'Each box separately']].map(([m, lbl]) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                mode === m ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
              }`}>
              {lbl}
            </button>
          ))}
        </div>
      )}

      {perBox ? (
        <>
          <div className="border rounded-lg divide-y mb-3">
            {boxes.map((b, i) => {
              const v = boxVals[b.id] || {};
              const st = boxStats[i];
              return (
                <div key={b.id} className="p-3">
                  <p className="text-xs text-gray-500 mb-2">
                    Box {b.piece_no} · <span className="font-mono text-gray-700">{b.barcode}</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[['Weight (kg)', 'weight'], ['Length (cm)', 'length'],
                      ['Width (cm)', 'width'],   ['Height (cm)', 'height']].map(([lbl, k]) => (
                      <div key={k}>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">{lbl}</label>
                        <input type="number" step="0.1" min="0" value={v[k] ?? ''} onChange={setBox(b.id, k)}
                          className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                      </div>
                    ))}
                  </div>
                  {/* this box on its own */}
                  <p className={`text-xs mt-2 ${st.measured ? 'text-gray-600' : 'text-amber-600'}`}>
                    {st.measured ? (
                      <>
                        Scale <strong>{st.weight.toFixed(1)} kg</strong>
                        {st.vol > 0 && <> · Volumetric <strong>{st.vol.toFixed(1)} kg</strong> <span className="text-gray-400">({st.L}×{st.W}×{st.H} ÷ 5000)</span></>}
                        {' '}· charge on <strong className="text-orange-700">{st.chargeable.toFixed(1)} kg</strong>
                        {st.vol > st.weight && st.weight > 0 && <span className="text-gray-400"> (volumetric is heavier)</span>}
                      </>
                    ) : 'Not measured yet — enter the weight, or all three sizes'}
                  </p>
                </div>
              );
            })}
          </div>

          {/* All boxes together */}
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-3 py-1.5">Box</th>
                  <th className="text-right px-3 py-1.5">Scale kg</th>
                  <th className="text-right px-3 py-1.5">L × W × H cm</th>
                  <th className="text-right px-3 py-1.5">Volumetric kg</th>
                  <th className="text-right px-3 py-1.5">Charge kg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {boxStats.map((x) => (
                  <tr key={x.id} className={x.measured ? '' : 'text-amber-600'}>
                    <td className="px-3 py-1.5">Box {x.piece_no} <span className="font-mono text-gray-400">{x.barcode}</span></td>
                    <td className="px-3 py-1.5 text-right">{x.weight ? x.weight.toFixed(1) : '—'}</td>
                    <td className="px-3 py-1.5 text-right">{x.vol > 0 ? `${x.L} × ${x.W} × ${x.H}` : '—'}</td>
                    <td className="px-3 py-1.5 text-right">{x.vol > 0 ? x.vol.toFixed(1) : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{x.measured ? x.chargeable.toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-orange-50 font-semibold text-gray-800">
                <tr>
                  <td className="px-3 py-2">Total · {boxTotals.measured} of {boxes.length} boxes measured</td>
                  <td className="px-3 py-2 text-right">{boxTotals.weight.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-gray-400 font-normal">added per box</td>
                  <td className="px-3 py-2 text-right">{boxTotals.vol.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-orange-700">{chargeable.toFixed(1)}</td>
                </tr>
              </tfoot>
            </table>
            <p className="text-[11px] text-gray-400 mt-1">
              The parcel is charged on the heavier of the two totals: all boxes on the scale ({boxTotals.weight.toFixed(1)} kg)
              or all their volumetric weights added up ({boxTotals.vol.toFixed(1)} kg).
            </p>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          {[['Weight (kg)', weight, setWeight], ['Length (cm)', length, setLength],
            ['Width (cm)', width, setWidth],    ['Height (cm)', height, setHeight]].map(([lbl, val, set]) => (
            <div key={lbl}>
              <label className="block text-xs font-medium text-gray-500 mb-1">{lbl}</label>
              <input type="number" step="0.1" min="0" value={val} onChange={(e) => set(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          ))}
        </div>
      )}

      {chargeable > 0 && (
        <div className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3 mb-3 text-sm text-gray-700">
          {perBox ? <>{boxes.length} boxes, charge on <strong>{chargeable.toFixed(1)} kg</strong></> : <>
          Scale: <strong>{w.toFixed(1)} kg</strong>
          {volumetric > 0 && <> · Volumetric: <strong>{volumetric.toFixed(1)} kg</strong></>}
          {' '}→ charge on <strong>{chargeable.toFixed(1)} kg</strong></>}
          {matchedTier
            ? <> → tier <strong>{matchedTier.name}</strong> = <strong className="text-orange-700">R{parseFloat(matchedTier.price).toFixed(2)}</strong></>
            : <span className="text-red-600"> — no tier covers this weight, enter a manual price</span>}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Manual price (optional — overrides the tier)</label>
          <input type="number" step="0.01" min="0" value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder={matchedTier ? `Leave empty to use R${parseFloat(matchedTier.price).toFixed(2)}` : 'R'}
            className="w-64 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <button onClick={save} disabled={saving || (chargeable <= 0 && manual === '')}
          className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save & price parcel'}
        </button>
        {parcel.final_price && (
          <span className="text-xs text-gray-400 pb-2.5">Current price: R{parseFloat(parcel.final_price).toFixed(2)}</span>
        )}
      </div>

      {parcel.priced_by && (
        <p className="text-xs text-gray-500 mt-3 pt-3 border-t">
          {parcel.price_source === 'manual'
            ? <><span className="text-amber-600 font-medium">Manual price</span> set by </>
            : String(parcel.price_source || '').startsWith('over:')
              ? <><span className="text-gray-700 font-medium">{parcel.price_source.slice(5)}</span> · priced by </>
              : 'Priced by '}
          <strong className="text-gray-700">{parcel.priced_by}</strong>
          {' · '}
          {new Date(parcel.priced_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
          {' · '}
          <a href={`/activity-log?search=${parcel.tracking_number}`} className="text-orange-500 hover:underline">full history</a>
        </p>
      )}
    </div>
  );
}

/* ── Where it is collected and delivered ───────────────────────────── */
// The parcel follows the client's address unless the office sets one here:
// one-off ("only this parcel") or, when the client moved, written to the client.
function AddressChangeModal({ parcel, leg, tracking, onClose, onSaved }) {
  const isPickup = leg === 'pickup';
  const clientName = isPickup ? parcel.sender_name : parcel.recipient_name;
  const current = isPickup
    ? { address: parcel.sender_address, lat: parcel.sender_lat, lng: parcel.sender_lng, city: parcel.pickup_city }
    : { address: parcel.recipient_address, lat: parcel.recipient_lat, lng: parcel.recipient_lng, city: parcel.delivery_city };
  const [form, setForm] = useState({ address: current.address || '', city: current.city || '', latitude: current.lat || '', longitude: current.lng || '' });
  const [scope, setScope] = useState('parcel');   // 'parcel' | 'client'
  const [showMap, setShowMap] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.address.trim() && !(form.latitude && form.longitude)) return toast.error('Type the address, or drop a pin on the map');
    setBusy(true);
    try {
      await api.patch(`/parcels/${tracking}/address`, {
        leg, address: form.address.trim(), city: form.city, latitude: form.latitude || null, longitude: form.longitude || null,
        update_client: scope === 'client',
      });
      toast.success(scope === 'client' ? `${clientName}'s address updated — this and future parcels use it` : `${isPickup ? 'Pickup' : 'Delivery'} address changed for this parcel only`);
      onSaved(scope === 'client');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not change the address'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start overflow-y-auto p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 m-auto my-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Change the {isPickup ? 'pickup' : 'delivery'} address</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-gray-500">{isPickup ? 'Collected from' : 'Delivered to'} <strong>{clientName}</strong> · parcel {tracking}</p>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">New address</label>
          <AddressAutocomplete value={form.address} town={form.city}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onSelect={({ address, city, latitude, longitude }) =>
              setForm((f) => ({ ...f, address, city: city || f.city, latitude: latitude ?? f.latitude, longitude: longitude ?? f.longitude }))}
            placeholder="Start typing the street and number…" />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Town</label>
            <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Map pin</label>
            <div className="text-sm py-2">
              {form.latitude && form.longitude
                ? <span className="text-green-700">📍 {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}</span>
                : <span className="text-amber-600">No pin — the driver's map will search the address text</span>}
              {' '}<button type="button" onClick={() => setShowMap((v) => !v)} className="text-orange-600 hover:underline text-xs">{showMap ? 'hide map' : 'drop / move pin'}</button>
            </div>
          </div>
        </div>
        {showMap && (
          <Suspense fallback={<p className="text-xs text-gray-400">Loading map…</p>}>
            <LocationPicker lat={form.latitude} lng={form.longitude}
              defaultArea={/cape|bellville|durbanville|somerset|stellenbosch|paarl/i.test(form.city || '') ? 'capetown' : 'gansbaai'}
              onChange={async ({ lat, lng }) => {
                setForm((f) => ({ ...f, latitude: lat, longitude: lng }));
                if (lat && lng && !form.city) {
                  try { const { data } = await api.get('/geo/suburb', { params: { lat, lng } }); if (data?.town) setForm((f) => ({ ...f, city: f.city || data.town })); } catch { /* keep typing */ }
                }
              }} />
          </Suspense>
        )}

        <div className="space-y-2 pt-1">
          <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${scope === 'parcel' ? 'bg-orange-50 border-orange-300' : 'border-gray-200'}`}>
            <input type="radio" name="scope" checked={scope === 'parcel'} onChange={() => setScope('parcel')} className="mt-0.5 accent-orange-500" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Only this parcel</p>
              <p className="text-xs text-gray-500">A one-time address. {clientName}&apos;s record stays as it is.</p>
            </div>
          </label>
          <label className={`flex items-start gap-3 p-3 rounded-lg border ${can('clients.edit') ? 'cursor-pointer' : 'opacity-50'} ${scope === 'client' ? 'bg-orange-50 border-orange-300' : 'border-gray-200'}`}>
            <input type="radio" name="scope" checked={scope === 'client'} disabled={!can('clients.edit')} onChange={() => setScope('client')} className="mt-0.5 accent-orange-500" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Also update {clientName} permanently</p>
              <p className="text-xs text-gray-500">They moved — this becomes the client&apos;s address for every parcel from now on.</p>
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Cancel</button>
          <button onClick={save} disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save address'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddressesPanel({ parcel, tracking, qc }) {
  const [changing, setChanging] = useState(null);   // 'pickup' | 'delivery'
  const editable = can('parcels.book') && parcel.status !== 'cancelled' && parcel.status !== 'delivered';
  const pickupDone = parcel.status !== 'created';
  const refresh = (clientToo) => {
    qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    qc.invalidateQueries({ queryKey: ['parcels'] });
    if (clientToo) qc.invalidateQueries({ queryKey: ['clients'] });
  };
  const revertToClient = async (leg) => {
    try {
      await api.patch(`/parcels/${tracking}/address`, { leg, clear: true });
      toast.success(`Back to the client's ${leg} address`);
      refresh(false);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not reset the address'); }
  };
  const Row = ({ leg, label, who, address, lat, lng, overridden, clientAddress, done }) => (
    <div className="py-3 flex flex-wrap items-start gap-3">
      <div className="flex-1 min-w-[16rem]">
        <p className="text-xs font-semibold text-gray-400 uppercase">{label} · {who || '—'}</p>
        <p className="text-sm text-gray-800 mt-0.5">{address || <span className="text-amber-600">No address on file</span>}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">
          {lat && lng ? `📍 pin ${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}` : 'no map pin — the driver\'s map searches the text'}
          {overridden && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">changed for this parcel</span>}
          {overridden && clientAddress && <span className="ml-2">client record: {clientAddress}</span>}
        </p>
      </div>
      {editable && !done && (
        <div className="flex gap-2">
          <button onClick={() => setChanging(leg)} className="text-xs font-semibold border border-gray-300 hover:border-orange-400 rounded-lg px-3 py-1.5 text-gray-700">Change</button>
          {overridden && <button onClick={() => revertToClient(leg)} className="text-xs text-gray-500 hover:text-orange-600 underline">Use client&apos;s address again</button>}
        </div>
      )}
      {done && <span className="text-[11px] text-gray-400 self-center">{leg === 'pickup' ? 'collected' : 'delivered'}</span>}
    </div>
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-700 mb-1">📍 Pickup &amp; delivery addresses</h3>
      <p className="text-xs text-gray-400 mb-1">
        The driver&apos;s app, runsheet and route use these. Change one for this parcel only, or update the client when they moved.
        {parcel.address_changed_at && <> Last changed {new Date(parcel.address_changed_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}{parcel.address_changed_by_name ? ` by ${parcel.address_changed_by_name}` : ''}.</>}
      </p>
      <div className="divide-y">
        <Row leg="pickup" label="Pickup" who={parcel.sender_name} address={parcel.sender_address} lat={parcel.sender_lat} lng={parcel.sender_lng}
          overridden={parcel.pickup_override} clientAddress={parcel.sender_client_address} done={pickupDone} />
        <Row leg="delivery" label="Delivery" who={parcel.recipient_name} address={parcel.recipient_address} lat={parcel.recipient_lat} lng={parcel.recipient_lng}
          overridden={parcel.delivery_override} clientAddress={parcel.recipient_client_address} done={parcel.status === 'delivered'} />
      </div>
      {changing && (
        <AddressChangeModal parcel={parcel} leg={changing} tracking={tracking}
          onClose={() => setChanging(null)} onSaved={(clientToo) => { setChanging(null); refresh(clientToo); }} />
      )}
    </div>
  );
}

/* ── Extra items sold with the parcel ──────────────────────────────── */
function ExtrasPanel({ parcel, tracking, qc }) {
  const extras = parcel.extras || [];
  const locked = !!parcel.invoice_id;
  const { data: products = [] } = useQ({
    queryKey: ['billing-products'],
    queryFn: () => api.get('/billing/products').then((r) => r.data),
    enabled: can('billing.view'),
  });
  const [form, setForm] = useState({ description: '', quantity: '1', unit_price: '' });
  const [busy, setBusy] = useState(false);
  const total = extras.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  const add = async () => {
    if (!form.description.trim()) return toast.error('Describe the item, or pick one from the price list');
    setBusy(true);
    try {
      await api.post(`/parcels/${tracking}/extras`, { description: form.description.trim(), quantity: form.quantity || 1, unit_price: form.unit_price });
      toast.success('Item added — it goes on the invoice with this parcel');
      setForm({ description: '', quantity: '1', unit_price: '' });
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add the item'); }
    finally { setBusy(false); }
  };
  const remove = async (e) => {
    try {
      await api.delete(`/parcels/${tracking}/extras/${e.id}`);
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) { toast.error(err.response?.data?.error || 'Could not remove the item'); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-700 mb-1">🧾 Extra items sold with this parcel</h3>
      <p className="text-xs text-gray-400 mb-3">
        Bubble wrap, a Fastway label, packaging… Priced incl. VAT from your price list. They are billed as item lines on the
        same invoice as the parcel{locked ? ' — this parcel is on an invoice, so change them there.' : '.'}
      </p>
      {extras.length > 0 && (
        <div className="border rounded-lg divide-y mb-3">
          {extras.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span>{e.description}{parseFloat(e.quantity) !== 1 ? <span className="text-gray-400"> · {parseFloat(e.quantity)} × R{parseFloat(e.unit_price).toFixed(2)}</span> : null}
                {e.added_by ? <span className="text-gray-400 text-xs"> · {e.added_by}</span> : null}</span>
              <span className="flex items-center gap-3">
                <span className="font-semibold">R{parseFloat(e.amount).toFixed(2)}</span>
                {!locked && can('parcels.book') && (
                  <button onClick={() => remove(e)} className="text-gray-400 hover:text-red-600" title="Remove"><Trash2 size={14} /></button>
                )}
              </span>
            </div>
          ))}
          <div className="flex justify-between px-3 py-2 text-sm bg-gray-50 font-semibold">
            <span>Extras total</span><span>R{total.toFixed(2)}</span>
          </div>
        </div>
      )}
      {!locked && can('parcels.book') && (
        <div className="flex flex-wrap items-end gap-2">
          {products.length > 0 && (
            <select value="" onChange={(e) => { const p = products.find((x) => x.id === e.target.value); if (p) setForm({ description: p.name, quantity: '1', unit_price: parseFloat(p.unit_price).toFixed(2) }); }}
              className="border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
              <option value="">From the price list…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} — R{parseFloat(p.unit_price).toFixed(2)}</option>)}
            </select>
          )}
          <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Item, e.g. Bubble wrap"
            className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-orange-400" />
          <input type="number" min="1" step="1" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} title="Quantity"
            className="border rounded-lg px-2 py-2 text-sm w-16 text-right focus:outline-none focus:ring-2 focus:ring-orange-400" />
          <input type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))} placeholder="R each"
            className="border rounded-lg px-2 py-2 text-sm w-24 text-right focus:outline-none focus:ring-2 focus:ring-orange-400" />
          <button onClick={add} disabled={busy}
            className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm font-semibold">
            <Plus size={14} /> Add
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────── */

// Swap the sender or the recipient for another client — the parcel was booked
// against the wrong account, or a different company must be billed. Office
// only, and only while the parcel is not on an invoice. The payer follows.
function ChangePartyModal({ parcel, tracking, which, onClose, onDone }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: hits = [] } = useQ({
    queryKey: ['client-search', q],
    queryFn: () => api.get('/clients', { params: { search: q, limit: 15 } }).then((r) => r.data),
    enabled: q.trim().length >= 2,
  });
  const current = which === 'sender' ? parcel.sender_name : parcel.recipient_name;
  const save = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await api.patch(`/parcels/${tracking}/parties`, { [which === 'sender' ? 'sender_id' : 'recipient_id']: picked.id });
      toast.success(`${which === 'sender' ? 'Sender' : 'Recipient'} is now ${picked.name} · billed to ${r.data.bill_to_name}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not change it');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md my-8 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-gray-800">Change the {which}</h3>
        <p className="text-xs text-gray-500">Now: <strong>{current}</strong>. Pick the client it should be. If {current} was paying for this parcel, the new {which} pays instead.</p>
        <input autoFocus className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          placeholder="Type part of the client's name, phone or code…" value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} />
        {q.trim().length >= 2 && (
          <div className="border rounded-lg divide-y max-h-64 overflow-auto">
            {hits.filter((c) => c.id !== parcel.sender_id && c.id !== parcel.recipient_id).map((c) => (
              <button key={c.id} type="button" onClick={() => setPicked(c)}
                className={`block w-full text-left px-3 py-2 text-sm hover:bg-orange-50 ${picked?.id === c.id ? 'bg-orange-50 font-semibold' : ''}`}>
                {c.name}{c.business_name && c.business_name !== c.name ? <span className="text-gray-400"> · {c.business_name}</span> : null}
                <span className="block text-[11px] text-gray-400">{[c.phone, c.email, c.city].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
            {hits.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">No client matches.</p>}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Cancel</button>
          <button type="button" onClick={save} disabled={!picked || busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
            {busy ? 'Saving…' : picked ? `Make ${picked.name} the ${which}` : `Pick the new ${which}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ParcelDetail() {
  const { tracking } = useParams();
  const qc = useQueryClient();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [changeParty, setChangeParty] = useState(null);   // 'sender' | 'recipient' — the change-client popup
  const [scanType, setScanType]       = useState('');
  const [failReason, setFailReason]   = useState('');
  const [failNotes, setFailNotes]     = useState('');
  const [assignDriver, setAssignDriver] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [pickupDriver, setPickupDriver] = useState('');
  const [scanning, setScanning]       = useState(false);
  const [cancelling, setCancelling]   = useState(false);

  const { data: parcel, isLoading } = useQuery({
    queryKey: ['parcel', tracking],
    queryFn: () => api.get(`/parcels/${tracking}`).then((r) => r.data),
    staleTime: 0, // always fetch fresh — status changes must show immediately
  });

  const { data: drivers = [] } = useQ({
    queryKey: ['drivers'],
    queryFn: () => api.get('/users/drivers').then((r) => r.data),
  });

  // Pre-select the dropdowns with the parcel's current drivers so the page
  // shows who's assigned (and lets the admin change it).
  useEffect(() => {
    if (parcel) {
      setPickupDriver(parcel.pickup_driver_id || '');
      setAssignDriver(parcel.assigned_driver_id || '');
    }
  }, [parcel?.pickup_driver_id, parcel?.assigned_driver_id]);

  const doScan = async () => {
    if (!scanType) return toast.error('Select scan type');
    setScanning(true);
    try {
      await api.post(`/parcels/${tracking}/scan`, {
        event_type: scanType,
        failure_reason: failReason || undefined,
        failure_notes: failNotes || undefined,
      });
      toast.success('Scan recorded');
      setScanType(''); setFailReason(''); setFailNotes('');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const doAssignPickup = async () => {
    if (!pickupDriver) return toast.error('Select a driver');
    try {
      await api.patch(`/parcels/${tracking}/assign-pickup`, { driver_id: pickupDriver });
      toast.success('Pickup driver assigned');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) {
      toast.error('Assignment failed');
    }
  };

  const doCancel = async () => {
    if (!window.confirm(`Cancel parcel ${parcel.tracking_number}? This cannot be undone.`)) return;
    setCancelling(true);
    try {
      await api.patch(`/parcels/${tracking}/cancel`);
      toast.success('Parcel cancelled');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel parcel');
    } finally {
      setCancelling(false);
    }
  };

  const doAssign = async () => {
    if (!assignDriver) return toast.error('Select a driver');
    try {
      await api.patch(`/parcels/${tracking}/assign`, { driver_id: assignDriver, scheduled_delivery: deliveryDate || undefined });
      toast.success('Driver assigned');
      setAssignDriver(''); setDeliveryDate('');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) {
      toast.error('Assignment failed');
    }
  };

  if (isLoading) return <div className="text-center py-20 text-gray-400">Loading...</div>;
  if (!parcel) return <div className="text-center py-20 text-gray-500">Parcel not found</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="bg-gray-900 text-white rounded-xl p-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs text-gray-400 mb-1">Tracking Number</p>
          <p className="text-2xl font-mono font-bold">{parcel.tracking_number}</p>
          <span className={`inline-block mt-2 text-xs px-3 py-1 rounded-full font-medium ${STATUS_COLORS[parcel.status]}`}>
            {STATUS_LABELS[parcel.status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {can('parcels.cancel') && !['delivered', 'cancelled'].includes(parcel.status) && (
            <button onClick={doCancel} disabled={cancelling}
              className="flex items-center gap-1 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg disabled:opacity-50">
              <XCircle size={14} /> {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          <a
            href={`${process.env.REACT_APP_API_URL || 'http://192.168.127.146:3001'}/labels/${parcel.tracking_number}.pdf`}
            target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg"
          >
            <Download size={14} /> Label
          </a>
        </div>
      </div>

      {/* Info */}
      <div className="bg-white rounded-xl shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Sender</p>
          <InfoRow label="Name" value={parcel.sender_name} />
          <InfoRow label="Phone" value={parcel.sender_phone} />
          <InfoRow label="Address" value={parcel.sender_address ? `${parcel.sender_address}${parcel.pickup_override ? ' (changed for this parcel)' : ''}` : null} />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Recipient</p>
          <InfoRow label="Name" value={parcel.recipient_name} />
          <InfoRow label="Phone" value={parcel.recipient_phone} />
          <InfoRow label="Address" value={parcel.recipient_address ? `${parcel.recipient_address}${parcel.delivery_override ? ' (changed for this parcel)' : ''}` : null} />
        </div>
        <div className="md:col-span-2 mt-4">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Parcel</p>
          <InfoRow label="Description" value={parcel.description} />
          <InfoRow label="Weight" value={parcel.weight ? `${parseFloat(parcel.weight).toFixed(1)} kg${parcel.chargeable_kg && parseFloat(parcel.chargeable_kg) > parseFloat(parcel.weight) ? ` on the scale · ${parseFloat(parcel.chargeable_kg).toFixed(1)} kg chargeable` : ''}` : null} />
          {parcel.pieces?.length > 1 && (
            <InfoRow label="Boxes" value={`${parcel.pieces.length} boxes` + (parcel.pieces.some((b) => b.weight) ? ` · ${parcel.pieces.map((b) => (b.weight ? `${parseFloat(b.weight).toFixed(1)} kg` : '—')).join(' + ')}` : '')} />
          )}
          <InfoRow label="Tier" value={parcel.tier_name} />
          <InfoRow label="Price" value={parcel.final_price ? `R${parseFloat(parcel.final_price).toFixed(2)}` : null} />
          <InfoRow label="Pickup Driver" value={parcel.pickup_driver_name} />
          <InfoRow label="Delivery Driver" value={parcel.driver_name} />
          <InfoRow label="Scheduled Delivery" value={parcel.scheduled_delivery ? new Date(parcel.scheduled_delivery).toLocaleDateString('en-ZA') : null} />
          <div className="flex items-start gap-2 py-1.5">
            <span className="text-sm text-gray-500 w-36 flex-shrink-0">Notes</span>
            <div className="flex-1">
              <NotesEditor key={parcel.notes || ''} value={parcel.notes} editable={can('parcels.book')}
                placeholder="Special instructions for this parcel — fragile, call first, leave at the gate…"
                onSave={async (notes) => {
                  await api.patch(`/parcels/${tracking}/notes`, { notes });
                  qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
                }} />
            </div>
          </div>
          {/* Who pays for this parcel — changeable until it's on an invoice */}
          <div className="flex items-center gap-2 py-1.5">
            <span className="text-sm text-gray-500 w-36 flex-shrink-0">Bill to (payer)</span>
            {can('parcels.book') && !parcel.invoice_id ? (
              <select
                value={parcel.bill_to_client_id || ''}
                onChange={async (e) => {
                  try {
                    await api.patch(`/parcels/${tracking}/bill-to`, { client_id: e.target.value });
                    toast.success('Payer updated');
                    qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'Failed to change payer');
                  }
                }}
                className="border rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value={parcel.sender_id}>{parcel.sender_name} (sender)</option>
                <option value={parcel.recipient_id}>{parcel.recipient_name} (recipient)</option>
              </select>
            ) : (
              <span className="text-sm font-medium text-gray-800">
                {parcel.bill_to_name || '—'}{parcel.invoice_id ? ' · already invoiced' : ''}
              </span>
            )}
            {can('parcels.book') && !parcel.invoice_id && parcel.status !== 'cancelled' && (
              <span className="ml-auto flex gap-3 text-xs">
                <button type="button" onClick={() => setChangeParty('sender')} className="text-orange-600 hover:underline">Change sender</button>
                <button type="button" onClick={() => setChangeParty('recipient')} className="text-orange-600 hover:underline">Change recipient</button>
              </span>
            )}
          </div>
        </div>
      </div>
      {changeParty && (
        <ChangePartyModal parcel={parcel} tracking={tracking} which={changeParty}
          onClose={() => setChangeParty(null)}
          onDone={() => { setChangeParty(null); qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] }); }} />
      )}

      {/* Several boxes on one pickup */}
      {can('parcels.book') && parcel.status !== 'cancelled' && (
        <BoxesPanel parcel={parcel} tracking={tracking} qc={qc} />
      )}

      {/* Where it is collected and delivered — office changes it here */}
      <AddressesPanel parcel={parcel} tracking={tracking} qc={qc} />

      {/* Depot weigh-in & pricing — locked once the parcel is invoiced */}
      {can('parcels.book') && !parcel.invoice_id && parcel.status !== 'cancelled' && (
        <WeighPricePanel parcel={parcel} tracking={tracking} qc={qc} />
      )}

      {/* Extra items sold with the parcel */}
      {can('parcels.book') && parcel.status !== 'cancelled' && (parcel.extras?.length > 0 || !parcel.invoice_id) && (
        <ExtrasPanel parcel={parcel} tracking={tracking} qc={qc} />
      )}

      {/* Send the sender their collection receipt */}
      {can('parcels.book') && parcel.status !== 'cancelled' && (
        <PickupProofPanel parcel={parcel} tracking={tracking} />
      )}

      {/* Proof of Delivery */}
      <ProofOfDelivery parcel={parcel} />

      {/* Did the driver actually reach the address? (from the location trail) */}
      {can('drivers.track') && parcel.assigned_driver_id &&
        ['out_for_delivery', 'delivered', 'failed_delivery', 'returned_to_depot'].includes(parcel.status) && (
        <ProximityCard tracking={tracking} />
      )}

      {/* WhatsApp */}
      <WhatsAppPanel parcel={parcel} />

      {/* Assign pickup driver (Cape Town collection) */}
      {!['delivered', 'cancelled'].includes(parcel.status) && can('parcels.assign') && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-700 mb-1 flex items-center gap-2"><Truck size={18} /> Assign Pickup Driver</h3>
          <p className="text-xs text-gray-400 mb-3">Driver who will collect from Cape Town sender</p>
          <div className="flex gap-3 flex-wrap">
            <select value={pickupDriver} onChange={(e) => setPickupDriver(e.target.value)}
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              <option value="">Select driver...</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <button onClick={doAssignPickup} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
              Assign
            </button>
          </div>
        </div>
      )}

      {/* Assign delivery driver */}
      {!['delivered', 'cancelled'].includes(parcel.status) && can('parcels.assign') && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-700 mb-1 flex items-center gap-2"><UserCheck size={18} /> Assign Delivery Driver</h3>
          <p className="text-xs text-gray-400 mb-3">Driver who will deliver to Gansbaai recipient</p>
          <div className="flex gap-3 flex-wrap">
            <select value={assignDriver} onChange={(e) => setAssignDriver(e.target.value)}
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              <option value="">Select driver...</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button onClick={doAssign} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
              Assign
            </button>
          </div>
        </div>
      )}

      {/* Scan event */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-semibold text-gray-700 mb-3">Record Scan Event</h3>
        <div className="flex gap-3 flex-wrap mb-3">
          {['collected', 'at_depot', 'out_for_delivery', 'delivered', 'failed_delivery', 'returned_to_depot'].map((s) => (
            <button
              key={s}
              onClick={() => setScanType(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                scanType === s ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 text-gray-600 hover:border-orange-400'
              }`}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        {scanType === 'failed_delivery' && (
          <div className="space-y-2 mb-3">
            <select value={failReason} onChange={(e) => setFailReason(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              <option value="">Select failure reason...</option>
              {FAILURE_REASONS.map((r) => <option key={r} value={r}>{FAILURE_LABELS[r]}</option>)}
            </select>
            <input type="text" value={failNotes} onChange={(e) => setFailNotes(e.target.value)}
              placeholder="Additional notes (optional)"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        )}
        <button onClick={doScan} disabled={!scanType || scanning}
          className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
        >
          {scanning ? 'Recording...' : 'Record Scan'}
        </button>
      </div>

      {/* Event history */}
      <EventHistory parcel={parcel} user={user} drivers={drivers} tracking={tracking} />

      {/* What the office did to it — separate from the scans above */}
      <ParcelChanges tracking={tracking} />
    </div>
  );
}

/* ── Was the driver near the address? ─────────────────────────────── */
// Reads the driver's location trail on the attempt day and reports the closest
// they came to the delivery address — the answer to "did he actually go there",
// which a late failed-delivery scan can't give.
function ProximityCard({ tracking }) {
  const { data, isLoading } = useQuery({
    queryKey: ['proximity', tracking],
    queryFn: () => api.get(`/parcels/${tracking}/proximity`).then((r) => r.data),
    retry: false,
  });
  if (isLoading || !data) return null;

  const when = (t) => new Date(t).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' });
  let tone = 'bg-gray-50 text-gray-600 border-gray-200', icon = '📍', line;
  if (data.status === 'reached') {
    tone = 'bg-green-50 text-green-800 border-green-200'; icon = '✅';
    line = <>Driver <strong>{data.driver_name}</strong> came within <strong>{data.closest_m}m</strong> of the address at <strong>{when(data.closest_at)}</strong>.</>;
  } else if (data.status === 'not_reached') {
    tone = 'bg-red-50 text-red-800 border-red-200'; icon = '⚠️';
    line = <>Driver <strong>{data.driver_name}</strong> was <strong>never within {data.radius}m</strong> of the address that day — closest was <strong>{data.closest_m}m</strong> at {when(data.closest_at)}.</>;
  } else {
    line = <span className="text-gray-500">{data.message}</span>;
  }
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${tone}`}>
      <span className="text-lg leading-none">{icon}</span>
      <div className="text-sm">
        <p className="font-semibold mb-0.5">Delivery location check</p>
        {line}
      </div>
    </div>
  );
}

/* ── Proof of collection, sent to the sender ──────────────────────────
   Suppliers asked for a receipt to keep on their own side. Nothing here
   fires by itself — the office presses the button — so a client is never
   messaged automatically. */
function PickupProofPanel({ parcel, tracking }) {
  const collected = (parcel.events || []).filter((e) => e.event_type === 'collected').pop();
  const [to, setTo] = useState('');
  const [channel, setChannel] = useState('email');
  const [busy, setBusy] = useState(false);

  const stored = channel === 'email' ? parcel.sender_email : parcel.sender_phone;
  const value = to || stored || '';
  const willSave = !stored && !!to;

  if (!collected) return null;

  const send = async () => {
    if (!value) return toast.error(`Enter ${parcel.sender_name}'s ${channel === 'email' ? 'email' : 'cell number'}`);
    setBusy(true);
    try {
      const { data } = await api.post(`/parcels/${tracking}/pickup-proof/send`, {
        channel, to: value,
      });
      if (data.contact_saved) toast.success(`Saved to ${parcel.sender_name} for next time`);
      if (channel === 'email') {
        toast.success(`Proof of collection emailed to ${data.to}`);
      } else {
        // wa.me cannot carry the PDF, so WhatsApp gets the text and a link
        window.open(data.url, '_blank');
        toast.success('WhatsApp opened — press send there');
      }
      setTo('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the proof');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-700 mb-1">📤 Proof of collection</h3>
      <p className="text-xs text-gray-400 mb-3">
        Send <strong>{parcel.sender_name}</strong> a receipt for the goods we collected
        {collected.signed_by && <> — signed by <strong>{collected.signed_by}</strong></>}.
        Email carries a PDF; WhatsApp carries a link.
      </p>

      <div className="flex gap-2 mb-3">
        {[['email', 'Email'], ['whatsapp', 'WhatsApp']].map(([k, lbl]) => (
          <button key={k} type="button" onClick={() => { setChannel(k); setTo(''); }}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              channel === k ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
            }`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-56">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {channel === 'email' ? 'Email address' : 'Cell number'}
          </label>
          <input type="text" value={value} onChange={(e) => setTo(e.target.value)}
            placeholder={channel === 'email' ? 'name@company.co.za' : '082 123 4567'}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <button onClick={send} disabled={busy}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {busy ? 'Sending…' : channel === 'email' ? 'Email proof' : 'Open WhatsApp'}
        </button>
      </div>

      <p className="text-xs mt-2">
        {willSave ? (
          <span className="text-orange-600">
            {parcel.sender_name} has no {channel === 'email' ? 'email' : 'cell number'} on file — this one will be saved to them.
          </span>
        ) : stored ? (
          <span className="text-gray-400">
            Saved on the client. Sending somewhere else this once will not change it.
          </span>
        ) : null}
      </p>
    </div>
  );
}

/* ── Proof of Delivery card ──────────────────────────────────────── */
function ProofOfDelivery({ parcel }) {
  const BASE = 'https://expressway.xcourier.co.za';

  // Find the delivered event that has proof attached
  const deliveredEvent = (parcel.events || [])
    .filter(e => e.event_type === 'delivered' && (e.photo_path || e.signature_path))
    .pop();

  if (!deliveredEvent) return null;

  const { photo_path, signature_path, latitude, longitude, scanned_at, driver_name,
          proof_type, signed_by, proof_note } = deliveredEvent;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-700 flex items-center gap-2">
          <span className="text-green-500">✅</span> Proof of Delivery
        </h3>
        <div className="text-xs text-gray-400 text-right">
          <p>{new Date(scanned_at).toLocaleString('en-ZA')}</p>
          {driver_name && <p className="font-medium text-gray-500">{driver_name}</p>}
        </div>
      </div>

      {/* How it was handed over. Older deliveries have no proof_type, so they
          simply show the photo and signature as before. */}
      {proof_type === 'left_at_spot' ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          <p className="text-sm font-semibold text-amber-900">📦 Left at the agreed place</p>
          {proof_note && <p className="text-xs text-amber-800 mt-0.5">{proof_note}</p>}
          <p className="text-[11px] text-amber-700 mt-1">
            Nobody signed — the photo is the proof the client asked us to rely on.
          </p>
        </div>
      ) : signed_by ? (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
          <p className="text-sm text-green-900">
            Handed over and signed by <strong>{signed_by}</strong>
          </p>
        </div>
      ) : null}

      {/* Photo + Signature side by side */}
      <div className="flex gap-4 flex-wrap mb-4">
        {photo_path && (
          <div className="flex-1 min-w-[140px]">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">📷 Delivery Photo</p>
            <a href={`${BASE}${photo_path}`} target="_blank" rel="noopener noreferrer">
              <img
                src={`${BASE}${photo_path}`}
                alt="Delivery photo"
                className="w-full max-h-64 object-cover rounded-xl border border-gray-100 shadow-sm hover:opacity-90 transition-opacity cursor-zoom-in"
              />
            </a>
          </div>
        )}
        {signature_path && (
          <div className="flex-1 min-w-[140px]">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">✍️ Customer Signature</p>
            <a href={`${BASE}${signature_path}`} target="_blank" rel="noopener noreferrer">
              <div className="w-full rounded-xl border border-gray-100 shadow-sm bg-gray-50 flex items-center justify-center p-3 hover:opacity-90 transition-opacity cursor-zoom-in" style={{ minHeight: 120 }}>
                <img
                  src={`${BASE}${signature_path}`}
                  alt="Customer signature"
                  className="max-h-40 object-contain"
                />
              </div>
            </a>
          </div>
        )}
      </div>

      {/* GPS location */}
      {latitude && longitude && (
        <a
          href={`https://www.google.com/maps?q=${latitude},${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg"
        >
          📍 View delivery location on Google Maps
          <span className="text-gray-400">({parseFloat(latitude).toFixed(5)}, {parseFloat(longitude).toFixed(5)})</span>
        </a>
      )}
    </div>
  );
}

/* ── Event history with inline driver correction ─────────────────── */
const CORRECTABLE = ['collected', 'delivered', 'failed_delivery'];

// datetime-local wants local wall-clock time without a zone
const toLocalInput = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function EventHistory({ parcel, user, drivers, tracking }) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [saving, setSaving] = useState(false);
  const [datingId, setDatingId] = useState(null);      // the scan whose date is being changed
  const [newWhen, setNewWhen] = useState('');
  const canBackdate = can('parcels.backdate');

  const saveDate = async (ev) => {
    const when = new Date(newWhen);
    if (!newWhen || Number.isNaN(when.getTime())) return toast.error('Pick a date and time');
    setSaving(true);
    try {
      const { data } = await api.patch(`/parcels/${tracking}/scan-events/${ev.id}/date`, { scanned_at: when.toISOString() });
      toast.success(data.booking_moved_to
        ? `Moved — booking date moved back to ${new Date(data.booking_moved_to).toLocaleDateString('en-ZA')} as well`
        : 'Scan moved');
      setDatingId(null);
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
      qc.invalidateQueries({ queryKey: ['parcels'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not move the scan');
    } finally {
      setSaving(false);
    }
  };

  const saveDriver = async (eventId) => {
    if (!selectedDriver) return toast.error('Select a driver');
    setSaving(true);
    try {
      const res = await api.patch(`/parcels/${tracking}/scan-events/${eventId}`, { driver_id: selectedDriver });
      toast.success(`Updated — credited to ${res.data.driver_name}`);
      setEditingId(null);
      setSelectedDriver('');
      qc.invalidateQueries({ queryKey: ['parcel', tracking] }); qc.invalidateQueries({ queryKey: ['parcel-changes', tracking] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-700 mb-3">Tracking History</h3>
      <div className="space-y-3">
        {(parcel.events || []).map((e) => (
          <div key={e.id} className="flex items-start gap-3">
            <div className={`mt-0.5 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[e.event_type]}`}>
              {STATUS_LABELS[e.event_type]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {datingId === e.id ? (
                  /* ── Back-date: pick the real date and time of this scan ── */
                  <div className="flex items-center gap-1.5">
                    <input type="datetime-local" autoFocus value={newWhen} max={toLocalInput(new Date().toISOString())}
                      onChange={(ev) => setNewWhen(ev.target.value)}
                      className="border rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    <button onClick={() => saveDate(e)} disabled={saving} title="Save the new date"
                      className="p-1 rounded bg-green-500 hover:bg-green-600 text-white disabled:opacity-50"><Check size={12} /></button>
                    <button onClick={() => setDatingId(null)} className="p-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-600"><X size={12} /></button>
                    <span className="text-[11px] text-gray-400">Booking date moves back too if this is earlier</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    {new Date(e.scanned_at).toLocaleString('en-ZA')}
                    {canBackdate && (
                      <button onClick={() => { setDatingId(e.id); setEditingId(null); setNewWhen(toLocalInput(e.scanned_at)); }}
                        title="Change the date and time of this scan (back-date)"
                        className="p-0.5 rounded text-gray-300 hover:text-orange-500 hover:bg-orange-50 transition-colors">
                        <Pencil size={11} />
                      </button>
                    )}
                  </p>
                )}
                {datingId === e.id ? null : editingId === e.id ? (
                  /* ── Inline driver picker ── */
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <select
                      autoFocus
                      value={selectedDriver}
                      onChange={(ev) => setSelectedDriver(ev.target.value)}
                      className="border rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="">Select driver…</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                    <button onClick={() => saveDriver(e.id)} disabled={saving}
                      className="p-1 rounded bg-green-500 hover:bg-green-600 text-white disabled:opacity-50">
                      <Check size={12} />
                    </button>
                    <button onClick={() => { setEditingId(null); setSelectedDriver(''); }}
                      className="p-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-600">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  /* ── Driver name + optional edit icon ── */
                  <div className="flex items-center gap-1">
                    {e.driver_name && (
                      <span className="text-xs text-gray-600 font-medium">· {e.driver_name}</span>
                    )}
                    {can('parcels.cancel') && CORRECTABLE.includes(e.event_type) && (
                      <button
                        onClick={() => { setEditingId(e.id); setSelectedDriver(''); }}
                        title="Correct driver"
                        className="p-0.5 rounded text-gray-300 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                      >
                        <Pencil size={11} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {e.failure_reason && (
                <p className="text-xs text-red-600 mt-0.5">
                  {FAILURE_LABELS[e.failure_reason]}{e.failure_notes ? ` — ${e.failure_notes}` : ''}
                </p>
              )}
              {/* GPS coordinates */}
              {e.latitude && e.longitude && (
                <a
                  href={`https://www.google.com/maps?q=${e.latitude},${e.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-0.5"
                >
                  📍 {parseFloat(e.latitude).toFixed(5)}, {parseFloat(e.longitude).toFixed(5)}
                </a>
              )}
              {/* Proof of delivery: photo + signature */}
              {(e.photo_path || e.signature_path) && (
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  {e.photo_path && (
                    <a href={`https://expressway.xcourier.co.za${e.photo_path}`} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`https://expressway.xcourier.co.za${e.photo_path}`}
                        alt="Delivery photo"
                        className="h-16 w-16 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity cursor-zoom-in"
                      />
                    </a>
                  )}
                  {e.signature_path && (
                    <a href={`https://expressway.xcourier.co.za${e.signature_path}`} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`https://expressway.xcourier.co.za${e.signature_path}`}
                        alt="Customer signature"
                        className="h-16 w-24 object-contain rounded-lg border border-gray-200 bg-white hover:opacity-80 transition-opacity cursor-zoom-in"
                      />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
